import { Link } from 'react-router-dom';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  StatCard,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { ChannelMeter, LicenseStatusBadge } from '@/components/License';
import { formatDay, formatMoney } from '@/lib/format';
import {
  BILLING_MODE_LABELS,
  daysLeftText,
  useMyLicense,
} from '@/lib/license';

/**
 * What this account has bought, how much of it is in use, and until when.
 *
 * Read-only by design: channels, prices and expiry are set by the platform.
 * The page exists so a client never learns their licence lapsed from the
 * dialer showing every lead coming back unanswered.
 */
export default function Subscription() {
  const { data, isPending, error, refetch } = useMyLicense();

  return (
    <div className="w-full">
      <PageHeader
        title="Subscription"
        description="Your channels, how many are in use right now, and when your licence renews."
      />

      {isPending ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} retry={() => refetch()} />
      ) : !data.enforced ? (
        <Card>
          <EmptyState
            title="No channel licence on this account"
            description={`This account is billed ${BILLING_MODE_LABELS[data.billing_mode].toLowerCase()}. Talk to your account manager to move to a monthly channel licence.`}
          />
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              label="Status"
              value={<LicenseStatusBadge status={data.status} />}
              hint={data.status === 'expired' ? 'New calls are not being answered' : undefined}
            />
            <StatCard
              label={data.status === 'expired' || data.status === 'grace' ? 'Expired on' : 'Renews on'}
              value={data.expires_at ? formatDay(data.expires_at) : 'No expiry'}
              hint={daysLeftText(data)}
              tone={data.status === 'expired' ? 'bad' : data.status === 'expiring' || data.status === 'grace' ? 'warn' : 'default'}
            />
            <StatCard
              label="Monthly total"
              value={formatMoney(data.monthly_total, data.currency)}
              hint={BILLING_MODE_LABELS[data.billing_mode]}
            />
            <StatCard
              label="Channels"
              value={data.engines.reduce((sum, e) => sum + e.channels, 0)}
              hint={`${data.engines.reduce((sum, e) => sum + e.in_use, 0)} in use now`}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card
              title="Channels"
              description="One channel is one call at a time. Calls beyond your channels are returned to the dialer, not lost."
              className="lg:col-span-2"
            >
              <div className="space-y-5">
                {data.engines.map((engine) => (
                  <div key={engine.kind} className="grid items-center gap-3 sm:grid-cols-[1fr_auto]">
                    <ChannelMeter engine={engine} />
                    <div className="text-right text-xs text-ink-500 dark:text-ink-400 sm:w-40">
                      {engine.channels > 0 ? (
                        <>
                          {formatMoney(engine.monthly_price, data.currency)} per channel
                          <br />
                          <span className="font-medium text-ink-700 dark:text-ink-200">
                            {formatMoney(engine.monthly_price * engine.channels, data.currency)} / month
                          </span>
                        </>
                      ) : (
                        'Not part of your plan'
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="How you are billed">
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-xs text-ink-500 dark:text-ink-400">Billing</dt>
                  <dd className="mt-0.5 font-medium text-ink-900 dark:text-ink-50">
                    {BILLING_MODE_LABELS[data.billing_mode]}
                  </dd>
                  <dd className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                    {data.shows_usage_costs
                      ? 'Usage is charged on top of your channels and shown on the Usage page.'
                      : 'One flat price per channel. Calls carry no per-minute charge.'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-500 dark:text-ink-400">Grace period</dt>
                  <dd className="mt-0.5 text-ink-900 dark:text-ink-50">
                    {data.grace_days > 0
                      ? `${data.grace_days} day${data.grace_days === 1 ? '' : 's'} after expiry`
                      : 'None — calls stop when the licence ends'}
                  </dd>
                </div>
              </dl>
            </Card>
          </div>

          <Card
            title="Invoices to pay"
            description="Paying a renewal invoice extends your licence by the period it covers."
            actions={
              <Link to="/billing" className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">
                All invoices
              </Link>
            }
            padded={false}
          >
            {(data.open_invoices ?? []).length === 0 ? (
              <EmptyState title="Nothing to pay" description="Your renewal invoice appears here a week before your licence ends." />
            ) : (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>Invoice</TH>
                      <TH>Covers</TH>
                      <TH>Due</TH>
                      <TH numeric>Balance</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {(data.open_invoices ?? []).map((invoice) => (
                      <TR key={invoice.id}>
                        <TD className="font-mono text-xs">{invoice.number}</TD>
                        <TD>
                          {formatDay(invoice.period_start)} – {formatDay(invoice.period_end)}
                        </TD>
                        <TD>
                          {formatDay(invoice.due_at)}{' '}
                          {invoice.due_at && new Date(invoice.due_at) < new Date() && (
                            <Badge tone="red">Overdue</Badge>
                          )}
                        </TD>
                        <TD numeric className="font-medium">
                          {formatMoney(invoice.balance, invoice.currency)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </Card>

        </div>
      )}
    </div>
  );
}
